import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  Skeleton,
  StatusBadge,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  formatDateTime,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import {
  Download,
  FileCheck2,
  History,
  ListChecks,
  Receipt,
  RotateCcw,
  XCircle,
} from 'lucide-react';
import { type JSX, useState } from 'react';

import { ApiError } from '../../api/client';
import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import { formatPriceCents } from '../../contracts/format-price';
import { TAX_CONDITION_LABELS, type TaxCondition } from '../../customers/api';
import { getFiscalModeBannerText } from '../../invoices/fiscal-mode-banner';
import { GuideLink } from '../../guide/guide-link';
import {
  downloadInvoicePdf,
  excludeInvoiceItem,
  getFiscalMode,
  getInvoice,
  getInvoiceArcaLogs,
  issueInvoice,
  reconcileInvoice,
  revertInvoiceToDraft,
  type Invoice,
  type InvoiceItem,
} from '../../invoices/api';
import { formatInvoiceNumber } from '../../invoices/format-invoice-number';
import { mapInvoiceError } from '../../invoices/invoice-api-errors';
import { formatCalendarDate } from '../../orders/format-calendar-date';

type DetailTab = 'items' | 'arca';

const invoiceDetailRoute = getRouteApi('/protected/invoices/$id');

const TAB_LABELS: Record<DetailTab, string> = { items: 'Ítems', arca: 'ARCA' };
const ARCA_OPERATION_LABELS: Record<string, string> = {
  FECAESolicitar: 'Emisión de la factura',
  FECompConsultar: 'Consulta del estado de la factura',
};

/** Statuses that already carry a CAE, so a PDF exists. */
const PDF_STATUSES: Invoice['status'][] = ['ISSUED', 'PARTIALLY_PAID', 'PAID', 'OVERDUE'];

const TAB_ICONS: Record<DetailTab, typeof ListChecks> = { items: ListChecks, arca: History };

/** `"21.00"` (`vatBreakdown` key, `calculate-invoice-totals.ts`) → `"IVA 21%"`; keeps a decimal only for non-integer rates (e.g. `10.5`). */
function formatVatRateLabel(key: string): string {
  const rate = Number(key);

  return Number.isInteger(rate) ? `IVA ${rate}%` : `IVA ${rate.toFixed(1)}%`;
}

/**
 * `billing-module/design.md` §9.4. "Excluir" is one button per row (this
 * PR's invoices always carry a single line in the seed/manual-test data,
 * but the endpoint is per-`deliveryNoteId`, so the trigger has to be too).
 * Mutation `onSuccess` writes the returned `Invoice` straight into the
 * `['invoice', id]` cache instead of invalidating: `apps/api` doesn't echo
 * state changes on a plain `GET` fast enough to matter here, and the
 * mutation response is already the authoritative next state.
 */
export function InvoiceDetailPage(): JSX.Element {
  const { id } = invoiceDetailRoute.useParams();
  const [tab, setTab] = useState<DetailTab>('items');
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currentUser = useCurrentUser();

  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [excludeItem, setExcludeItem] = useState<InvoiceItem | undefined>(undefined);
  const [excludeReason, setExcludeReason] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | undefined>(undefined);

  const query = useQuery({ queryKey: ['invoice', id], queryFn: () => getInvoice(id) });
  const fiscalModeQuery = useQuery({
    queryKey: ['invoices', 'fiscal-mode'],
    queryFn: getFiscalMode,
  });

  const arcaLogsQuery = useQuery({
    queryKey: ['invoice', id, 'arca-logs'],
    queryFn: () => getInvoiceArcaLogs(id),
    enabled: tab === 'arca',
  });

  function setInvoice(updated: Invoice): void {
    queryClient.setQueryData(['invoice', id], updated);
  }

  const issueMutation = useMutation({
    mutationFn: () => issueInvoice(id),
    onSuccess: (updated) => {
      setInvoice(updated);
      setIssueModalOpen(false);
      toast('success', 'Factura emitida');
    },
    // A timeout or auth failure leaves the invoice PENDING_ARCA on the server.
    onError: () => void queryClient.invalidateQueries({ queryKey: ['invoice', id] }),
  });

  const reconcileMutation = useMutation({
    mutationFn: () => reconcileInvoice(id),
    onSuccess: (updated) => {
      setInvoice(updated);
      toast('success', 'Factura reconciliada');
    },
  });

  const revertMutation = useMutation({
    mutationFn: () => revertInvoiceToDraft(id),
    onSuccess: (updated) => {
      setInvoice(updated);
      toast('success', 'Factura vuelta a borrador');
    },
  });

  const excludeMutation = useMutation({
    mutationFn: (reason: string) => {
      if (excludeItem === undefined) {
        return Promise.reject(new Error('No hay ningún ítem seleccionado para excluir.'));
      }

      return excludeInvoiceItem(id, excludeItem.deliveryNoteId, reason);
    },
    onSuccess: (updated) => {
      setInvoice(updated);
      setExcludeItem(undefined);
      setExcludeReason('');
      toast('success', 'Remito excluido');
    },
  });

  async function handleDownloadPdf(invoice: Invoice): Promise<void> {
    setDownloadError(undefined);
    setDownloading(true);

    try {
      await downloadInvoicePdf(
        invoice.id,
        formatInvoiceNumber(invoice.pointOfSale, invoice.invoiceType, invoice.number),
      );
    } catch {
      setDownloadError('No pudimos descargar el PDF de la factura.');
    } finally {
      setDownloading(false);
    }
  }

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 404) {
      return (
        <Alert tone="danger" title="Factura no encontrada">
          <Link to="/invoices" className="underline">
            Volver al listado
          </Link>
        </Alert>
      );
    }

    return (
      <Alert tone="danger" title="No pudimos cargar la factura">
        <div className="flex items-center justify-between gap-4">
          <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  const invoice = query.data;
  const canCreate = hasPermission(currentUser.data, 'invoice.create');
  const canIssue = hasPermission(currentUser.data, 'invoice.issue');
  const canSeeAccountStatement = hasPermission(currentUser.data, 'payment.read');
  const canExclude = invoice.status === 'DRAFT' && canCreate;

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Receipt}
        title={formatInvoiceNumber(invoice.pointOfSale, invoice.invoiceType, invoice.number)}
        description={`${invoice.customerLegalNameSnapshot} · ${formatCalendarDate(invoice.periodStart)} – ${formatCalendarDate(invoice.periodEnd)}`}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge domain="invoice" status={invoice.status} />
            <GuideLink moduleId="billing" />
          </div>
        }
      />

      {invoice.isTestInvoice ? (
        <Alert tone="warning" title="Modo de prueba: los comprobantes no tienen validez fiscal">
          Esta factura se emitió en modo de prueba, sin conexión real con ARCA. Su CAE (el código de autorización que ARCA da a cada factura) es simulado.
        </Alert>
      ) : invoice.cae === null && fiscalModeQuery.data?.isTestMode === true ? (
        <Alert tone="warning" title={getFiscalModeBannerText(fiscalModeQuery.data?.environment)}>
          El sistema todavía está en modo de prueba, sin conexión real con ARCA: al emitir, el CAE será simulado.
        </Alert>
      ) : null}

      {invoice.status === 'DRAFT' && invoice.hasIncompleteFiscalData ? (
        <Alert tone="warning" title="Faltan datos fiscales del cliente">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>Sin esos datos, ARCA puede rechazar la factura al emitirla.</span>
            <Link
              to="/customers/$customerId"
              params={{ customerId: invoice.customerId }}
              className="underline"
            >
              Completar datos del cliente
            </Link>
          </div>
        </Alert>
      ) : null}

      {reconcileMutation.isError ? (
        <Alert tone="danger">{mapInvoiceError(reconcileMutation.error)}</Alert>
      ) : null}
      {revertMutation.isError ? (
        <Alert tone="danger">{mapInvoiceError(revertMutation.error)}</Alert>
      ) : null}
      {downloadError !== undefined ? <Alert tone="danger">{downloadError}</Alert> : null}

      {invoice.status === 'REJECTED' && invoice.arcaErrorDetail !== null ? (
        <Alert tone="danger" title="Observaciones de ARCA">
          {invoice.arcaErrorDetail.message}
        </Alert>
      ) : null}

      <Card>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm text-slate-500">Cliente</dt>
            <dd className="text-sm font-medium text-slate-900">
              {invoice.customerLegalNameSnapshot}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-slate-500">CUIT</dt>
            <dd className="text-sm font-medium text-slate-900">{invoice.customerTaxIdSnapshot}</dd>
          </div>
          <div>
            <dt className="text-sm text-slate-500">Condición frente al IVA</dt>
            <dd className="text-sm font-medium text-slate-900">
              {TAX_CONDITION_LABELS[invoice.customerTaxConditionSnapshot as TaxCondition] ??
                invoice.customerTaxConditionSnapshot}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-slate-500">CAE</dt>
            <dd className="text-sm font-medium text-slate-900">{invoice.cae ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-sm text-slate-500">Saldo pendiente</dt>
            <dd className="text-sm font-medium text-slate-900">
              {formatPriceCents(invoice.pendingBalanceCents)}
            </dd>
          </div>
          {canSeeAccountStatement ? (
            <div>
              <dt className="text-sm text-slate-500">Cuenta corriente</dt>
              <dd className="text-sm font-medium text-slate-900">
                <Link
                  to="/customers/$customerId"
                  params={{ customerId: invoice.customerId }}
                  search={{ tab: 'cuenta-corriente' }}
                  className="underline"
                >
                  Cuenta corriente
                </Link>
              </dd>
            </div>
          ) : null}
        </dl>
      </Card>

      <Tabs value={tab} onValueChange={(value) => setTab(value as DetailTab)}>
        <TabList aria-label="Pestañas de la factura">
          {(Object.keys(TAB_LABELS) as DetailTab[]).map((key) => (
            <Tab key={key} value={key} icon={TAB_ICONS[key]}>
              {TAB_LABELS[key]}
            </Tab>
          ))}
        </TabList>

        <TabPanel value="items">
          <div className="flex flex-col gap-4">
            <Table>
              <caption className="sr-only">Ítems de la factura</caption>
              <thead>
                <tr>
                  <TableHeader>Ítem</TableHeader>
                  <TableHeader>Sede</TableHeader>
                  <TableHeader>Cantidad</TableHeader>
                  <TableHeader>Precio unitario</TableHeader>
                  <TableHeader>Total</TableHeader>
                  {canExclude ? <TableHeader>Acciones</TableHeader> : null}
                </tr>
              </thead>
              <TableBody>
                {invoice.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.descriptionSnapshot}</TableCell>
                    <TableCell>{item.siteNameSnapshot}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{formatPriceCents(item.unitPriceCents)}</TableCell>
                    <TableCell>
                      {formatPriceCents(item.netAmountCents + item.vatAmountCents)}
                    </TableCell>
                    {canExclude ? (
                      <TableCell>
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={XCircle}
                          onClick={() => setExcludeItem(item)}
                        >
                          Excluir
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Card>
              <dl className="flex flex-col gap-2">
                {Object.entries(invoice.vatBreakdown).map(([rate, amounts]) => (
                  <div key={rate} className="flex items-center justify-between text-sm">
                    <dt>{formatVatRateLabel(rate)}</dt>
                    <dd>
                      Neto {formatPriceCents(amounts.netCents)} · IVA{' '}
                      {formatPriceCents(amounts.vatCents)}
                    </dd>
                  </div>
                ))}
                <div className="flex items-center justify-between border-t border-slate-200 pt-2 text-sm font-semibold">
                  <dt>Total</dt>
                  <dd>{formatPriceCents(invoice.totalAmountCents)}</dd>
                </div>
              </dl>
            </Card>
          </div>
        </TabPanel>

        <TabPanel value="arca">
          {arcaLogsQuery.isPending ? <Skeleton className="h-32 w-full" /> : null}
          {arcaLogsQuery.data !== undefined && arcaLogsQuery.data.length === 0 ? (
            <EmptyState
              icon={History}
              title="Todavía no hay solicitudes a ARCA para esta factura."
            />
          ) : null}
          {arcaLogsQuery.data !== undefined && arcaLogsQuery.data.length > 0 ? (
            <Table>
              <caption className="sr-only">Historial ARCA</caption>
              <thead>
                <tr>
                  <TableHeader>Operación</TableHeader>
                  <TableHeader>Fecha</TableHeader>
                  <TableHeader>Resultado</TableHeader>
                </tr>
              </thead>
              <TableBody>
                {arcaLogsQuery.data.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {ARCA_OPERATION_LABELS[log.operation] ?? 'Consulta a ARCA'}
                    </TableCell>
                    <TableCell>{formatDateTime(log.createdAt)}</TableCell>
                    <TableCell>
                      {log.success
                        ? 'Correcto'
                        : (log.errorMessage ?? 'ARCA no aceptó la solicitud')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </TabPanel>
      </Tabs>

      <div className="flex flex-wrap items-center gap-3">
        {invoice.status === 'DRAFT' && canIssue ? (
          <Button icon={FileCheck2} onClick={() => setIssueModalOpen(true)}>
            Emitir
          </Button>
        ) : null}
        {invoice.status === 'PENDING_ARCA' && canIssue ? (
          <Button
            variant="secondary"
            icon={RotateCcw}
            loading={reconcileMutation.isPending}
            onClick={() => reconcileMutation.mutate()}
          >
            Reconciliar
          </Button>
        ) : null}
        {invoice.status === 'PENDING_ARCA' && canIssue ? (
          <span className="text-sm text-slate-500">
            Consulta a ARCA si la factura ya fue emitida.
          </span>
        ) : null}
        {invoice.status === 'REJECTED' && canCreate ? (
          <Button
            variant="secondary"
            icon={RotateCcw}
            loading={revertMutation.isPending}
            onClick={() => revertMutation.mutate()}
          >
            Volver a borrador
          </Button>
        ) : null}
        {PDF_STATUSES.includes(invoice.status) ? (
          <Button
            variant="secondary"
            icon={Download}
            loading={downloading}
            onClick={() => void handleDownloadPdf(invoice)}
          >
            Descargar PDF
          </Button>
        ) : null}
      </div>

      {issueModalOpen ? (
        <Modal
          open
          onClose={() => setIssueModalOpen(false)}
          title="Emitir factura"
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            {issueMutation.isError ? (
              <Alert tone="danger">{mapInvoiceError(issueMutation.error)}</Alert>
            ) : null}
            <p className="text-sm text-slate-600">
              Vas a solicitar el CAE ante ARCA para{' '}
              {formatInvoiceNumber(invoice.pointOfSale, invoice.invoiceType, invoice.number)}. Esta
              acción no se puede deshacer.
            </p>
            <div className="flex items-center gap-3">
              <Button loading={issueMutation.isPending} onClick={() => issueMutation.mutate()}>
                Confirmar emisión
              </Button>
              <Button variant="secondary" onClick={() => setIssueModalOpen(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {excludeItem !== undefined ? (
        <Modal
          open
          onClose={() => {
            setExcludeItem(undefined);
            setExcludeReason('');
          }}
          title="Excluir remito"
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            {excludeMutation.isError ? (
              <Alert tone="danger">{mapInvoiceError(excludeMutation.error)}</Alert>
            ) : null}
            <Field
              label="Motivo de la exclusión"
              hint="El remito vuelve a quedar sin facturar y podés facturarlo más adelante."
              name="excludeReason"
              required
              value={excludeReason}
              onChange={(event) => setExcludeReason(event.target.value)}
            />
            <div className="flex items-center gap-3">
              <Button
                loading={excludeMutation.isPending}
                disabled={excludeReason.trim() === ''}
                onClick={() => excludeMutation.mutate(excludeReason)}
              >
                Confirmar exclusión
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setExcludeItem(undefined);
                  setExcludeReason('');
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
